import React, { useEffect, useRef, useState } from "react";
import { api, API, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Video, Monitor, Camera, Loader2, Circle, Square, Trash2, Play, ExternalLink, RefreshCw, MonitorSmartphone } from "lucide-react";

const MODES = [
  { value: "camera", label: "Camera only", icon: Camera },
  { value: "screen", label: "Screen only", icon: Monitor },
  { value: "screen_camera", label: "Screen + camera", icon: MonitorSmartphone },
];
const AUDIO_OPTS = [
  { value: "mic", label: "Microphone" },
  { value: "system", label: "System audio" },
  { value: "both", label: "Mic + system" },
  { value: "none", label: "No audio" },
];
const MAX_SECONDS = 600; // 10 minutes
const CHUNK = 5 * 1024 * 1024;

function pickMime() {
  const c = ["video/webm;codecs=vp9,opus", "video/webm;codecs=vp8,opus", "video/webm", "video/mp4"];
  for (const m of c) { if (window.MediaRecorder && MediaRecorder.isTypeSupported(m)) return m; }
  return "video/webm";
}

function fmtTime(s) { const m = Math.floor(s / 60); const r = s % 60; return `${m}:${String(r).padStart(2, "0")}`; }

function capturePoster(blob) {
  return new Promise((resolve) => {
    let done = false;
    const src = URL.createObjectURL(blob);
    const v = document.createElement("video");
    const finish = (url) => { if (done) return; done = true; try { v.pause(); } catch (e) {} try { URL.revokeObjectURL(src); } catch (e) {} resolve(url); };
    const grab = () => {
      try {
        if (!v.videoWidth) return finish(null);
        const w = 480; const ratio = v.videoHeight / v.videoWidth || 0.5625;
        const c = document.createElement("canvas"); c.width = w; c.height = Math.round(w * ratio);
        c.getContext("2d").drawImage(v, 0, 0, c.width, c.height);
        finish(c.toDataURL("image/jpeg", 0.6));
      } catch (e) { finish(null); }
    };
    v.muted = true; v.playsInline = true; v.src = src;
    v.onloadeddata = () => { v.play().then(() => setTimeout(grab, 350)).catch(() => setTimeout(grab, 350)); };
    v.onerror = () => finish(null);
    setTimeout(() => (v.videoWidth ? grab() : finish(null)), 4000);
  });
}

export function VideoBrief({ base, apiClient = api, canRecord = true }) {
  const [briefs, setBriefs] = useState([]);
  const [driveReady, setDriveReady] = useState(true);
  const [open, setOpen] = useState(false);
  const [play, setPlay] = useState(null);

  const load = async () => {
    try {
      const { data } = await apiClient.get(`${base}/video-briefs`);
      setBriefs(data.briefs || []); setDriveReady(!!data.drive_ready);
    } catch (e) { /* silent — Drive may be off */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [base]);

  const del = async (id) => {
    try { await apiClient.delete(`/video-brief/${id}`); toast.success("Video brief removed"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="mb-3" data-testid="video-brief-section">
      {canRecord && (
        <Button type="button" size="sm" variant="outline" data-testid="add-video-brief-btn" onClick={() => setOpen(true)}
          className="border-[#d9ccff] bg-white text-[#5b2fd6] hover:bg-[#F6F2FF] rounded-full h-8">
          <Video className="h-4 w-4 mr-1.5" /> Add video brief
        </Button>
      )}
      {briefs.length > 0 && (
        <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
          {briefs.map((b) => (
            <div key={b.id} className="flex items-center gap-2 rounded-xl border border-[#EEEFEF] bg-[#F6F6FD] px-3 py-2" data-testid={`video-brief-${b.id}`}>
              <button type="button" onClick={() => setPlay(b)} data-testid={`video-brief-play-${b.id}`} className="relative h-12 w-16 rounded-lg overflow-hidden bg-[#06060C] flex items-center justify-center shrink-0 hover:opacity-90">
                {b.has_thumb && <img src={`${API}/video-briefs/${b.id}/thumb`} alt="" className="absolute inset-0 h-full w-full object-cover" loading="lazy" />}
                <span className="relative z-10 h-6 w-6 rounded-full bg-black/55 text-white flex items-center justify-center"><Play className="h-3.5 w-3.5" /></span>
              </button>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-medium text-[#06060C] truncate">{b.name}</p>
                <p className="text-[10px] text-[#9FA1A4]">{b.added_by || "Recorded"}</p>
              </div>
              {b.webViewLink && <a href={b.webViewLink} target="_blank" rel="noreferrer" className="text-[#9FA1A4] hover:text-[#5b2fd6]" title="View in Drive"><ExternalLink className="h-4 w-4" /></a>}
              <button type="button" onClick={() => del(b.id)} data-testid={`video-brief-delete-${b.id}`} className="text-[#9FA1A4] hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      )}
      {open && <RecorderDialog base={base} apiClient={apiClient} driveReady={driveReady} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
      {play && <PlayerDialog brief={play} onClose={() => setPlay(null)} />}
    </div>
  );
}

function PlayerDialog({ brief, onClose }) {
  const embed = brief.file_id ? `https://drive.google.com/file/d/${brief.file_id}/preview` : null;
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl bg-white" data-testid="video-brief-player">
        <DialogHeader><DialogTitle className="font-display truncate">{brief.name}</DialogTitle><DialogDescription className="text-[#9FA1A4]">Recorded video brief</DialogDescription></DialogHeader>
        {embed ? (
          <div className="aspect-video w-full"><iframe title="Video brief" src={embed} className="w-full h-full rounded-xl border border-[#EEEFEF]" allow="autoplay" allowFullScreen /></div>
        ) : (
          <a href={brief.webViewLink} target="_blank" rel="noreferrer" className="text-[#5b2fd6] hover:underline text-sm">Open in Google Drive</a>
        )}
      </DialogContent>
    </Dialog>
  );
}

function RecorderDialog({ base, apiClient, driveReady, onClose, onSaved }) {
  const [mode, setMode] = useState("camera");
  const [cams, setCams] = useState([]);
  const [mics, setMics] = useState([]);
  const [camId, setCamId] = useState("");
  const [micId, setMicId] = useState("");
  const [audioMix, setAudioMix] = useState("mic");
  const [step, setStep] = useState("setup"); // setup | recording | review | uploading
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState("");
  const [pct, setPct] = useState(0);
  const [previewUrl, setPreviewUrl] = useState(null);

  const liveRef = useRef(null);
  const reviewRef = useRef(null);
  const screenVideoRef = useRef(null);
  const camVideoRef = useRef(null);
  const canvasRef = useRef(null);

  const streamsRef = useRef([]);       // raw device streams to stop
  const finalStreamRef = useRef(null); // stream fed to MediaRecorder
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const blobRef = useRef(null);
  const rafRef = useRef(null);
  const timerRef = useRef(null);
  const audioCtxRef = useRef(null);
  const capStopRef = useRef(null);

  useEffect(() => {
    (async () => {
      if (!driveReady) return;
      try {
        const s = await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
        s.getTracks().forEach((t) => t.stop());
      } catch (e) { /* labels may be blank without permission */ }
      try {
        const devs = await navigator.mediaDevices.enumerateDevices();
        setCams(devs.filter((d) => d.kind === "videoinput"));
        setMics(devs.filter((d) => d.kind === "audioinput"));
      } catch (e) { /* ignore */ }
    })();
    return () => cleanup();
    /* eslint-disable-next-line */
  }, []);

  const stopRawStreams = () => {
    streamsRef.current.forEach((s) => { try { s.getTracks().forEach((t) => t.stop()); } catch (e) {} });
    streamsRef.current = [];
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    if (capStopRef.current) { try { capStopRef.current(); } catch (e) {} capStopRef.current = null; }
    if (audioCtxRef.current) { try { audioCtxRef.current.close(); } catch (e) {} audioCtxRef.current = null; }
  };

  const cleanup = () => {
    try { recorderRef.current && recorderRef.current.state !== "inactive" && recorderRef.current.stop(); } catch (e) {}
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    stopRawStreams();
    if (finalStreamRef.current) { try { finalStreamRef.current.getTracks().forEach((t) => t.stop()); } catch (e) {} finalStreamRef.current = null; }
    if (previewUrl) { try { URL.revokeObjectURL(previewUrl); } catch (e) {} }
  };

  const mixAudio = (streams) => {
    const withAudio = streams.filter((s) => s && s.getAudioTracks().length);
    if (!withAudio.length) return null;
    if (withAudio.length === 1) return withAudio[0].getAudioTracks()[0];
    const Ctx = window.AudioContext || window.webkitAudioContext;
    const ctx = new Ctx();
    audioCtxRef.current = ctx;
    const dest = ctx.createMediaStreamDestination();
    withAudio.forEach((s) => { try { ctx.createMediaStreamSource(s).connect(dest); } catch (e) {} });
    return dest.stream.getAudioTracks()[0];
  };

  // Build the composed stream + attach to the live preview
  const buildStream = async () => {
    setError("");
    stopRawStreams();
    if (finalStreamRef.current) { try { finalStreamRef.current.getTracks().forEach((t) => t.stop()); } catch (e) {} finalStreamRef.current = null; }
    const wantMic = audioMix === "mic" || audioMix === "both";
    const wantSys = audioMix === "system" || audioMix === "both";
    let videoTrack = null;
    const audioStreams = [];
    try {
      if (mode === "camera") {
        const cam = await navigator.mediaDevices.getUserMedia({
          video: { deviceId: camId ? { exact: camId } : undefined, width: { ideal: 1280 }, height: { ideal: 720 } },
          audio: wantMic ? { deviceId: micId ? { exact: micId } : undefined, echoCancellation: true, noiseSuppression: true } : false,
        });
        streamsRef.current.push(cam);
        videoTrack = cam.getVideoTracks()[0];
        if (wantMic) audioStreams.push(cam);
      } else {
        const screen = await navigator.mediaDevices.getDisplayMedia({ video: { frameRate: 30 }, audio: wantSys });
        streamsRef.current.push(screen);
        if (wantSys && screen.getAudioTracks().length) audioStreams.push(screen);
        let camStream = null;
        if (wantMic || mode === "screen_camera") {
          camStream = await navigator.mediaDevices.getUserMedia({
            video: mode === "screen_camera" ? { deviceId: camId ? { exact: camId } : undefined, width: { ideal: 640 }, height: { ideal: 480 } } : false,
            audio: wantMic ? { deviceId: micId ? { exact: micId } : undefined, echoCancellation: true, noiseSuppression: true } : false,
          });
          streamsRef.current.push(camStream);
          if (wantMic) audioStreams.push(camStream);
        }
        if (mode === "screen_camera") {
          // composite screen + camera bubble on a canvas
          const sv = screenVideoRef.current; const cv = camVideoRef.current; const canvas = canvasRef.current;
          sv.srcObject = new MediaStream(screen.getVideoTracks()); await sv.play().catch(() => {});
          cv.srcObject = new MediaStream(camStream.getVideoTracks()); await cv.play().catch(() => {});
          const W = 1280, H = 720; canvas.width = W; canvas.height = H;
          const ctx = canvas.getContext("2d");
          const draw = () => {
            try {
              ctx.fillStyle = "#000"; ctx.fillRect(0, 0, W, H);
              if (sv.videoWidth) ctx.drawImage(sv, 0, 0, W, H);
              const bw = W * 0.24, bh = bw * 0.75, bx = W - bw - 24, by = H - bh - 24;
              if (cv.videoWidth) {
                ctx.save();
                ctx.beginPath(); ctx.roundRect ? ctx.roundRect(bx, by, bw, bh, 14) : ctx.rect(bx, by, bw, bh); ctx.clip();
                ctx.drawImage(cv, bx, by, bw, bh);
                ctx.restore();
                ctx.strokeStyle = "rgba(255,255,255,0.85)"; ctx.lineWidth = 3;
                ctx.beginPath(); ctx.roundRect ? ctx.roundRect(bx, by, bw, bh, 14) : ctx.rect(bx, by, bw, bh); ctx.stroke();
              }
            } catch (e) {}
            rafRef.current = requestAnimationFrame(draw);
          };
          draw();
          const cap = canvas.captureStream(30);
          capStopRef.current = () => cap.getTracks().forEach((t) => t.stop());
          videoTrack = cap.getVideoTracks()[0];
          // stop recording if the user ends screen share
          screen.getVideoTracks()[0].addEventListener("ended", () => { if (recorderRef.current && recorderRef.current.state === "recording") stopRecording(); });
        } else {
          videoTrack = screen.getVideoTracks()[0];
          screen.getVideoTracks()[0].addEventListener("ended", () => { if (recorderRef.current && recorderRef.current.state === "recording") stopRecording(); });
        }
      }
    } catch (e) {
      setError(e?.name === "NotAllowedError" ? "Permission denied. Allow camera/screen access and try again." : (e?.message || "Couldn't start capture."));
      return null;
    }
    const tracks = [];
    if (videoTrack) tracks.push(videoTrack);
    const audioTrack = mixAudio(audioStreams);
    if (audioTrack) tracks.push(audioTrack);
    const final = new MediaStream(tracks);
    finalStreamRef.current = final;
    if (liveRef.current) { liveRef.current.srcObject = final; liveRef.current.muted = true; liveRef.current.play().catch(() => {}); }
    return final;
  };

  const startRecording = async () => {
    const final = await buildStream();
    if (!final) return;
    chunksRef.current = [];
    const mimeType = pickMime();
    let rec;
    try { rec = new MediaRecorder(final, { mimeType, videoBitsPerSecond: 2500000 }); }
    catch (e) { try { rec = new MediaRecorder(final); } catch (e2) { setError("Recording isn't supported in this browser."); return; } }
    recorderRef.current = rec;
    rec.ondataavailable = (ev) => { if (ev.data && ev.data.size) chunksRef.current.push(ev.data); };
    rec.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: rec.mimeType || mimeType });
      blobRef.current = blob;
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
      stopRawStreams();
      if (finalStreamRef.current) { try { finalStreamRef.current.getTracks().forEach((t) => t.stop()); } catch (e) {} finalStreamRef.current = null; }
      setStep("review");
    };
    rec.start(1000);
    setElapsed(0); setStep("recording");
    timerRef.current = setInterval(() => setElapsed((s) => { const n = s + 1; if (n >= MAX_SECONDS) stopRecording(); return n; }), 1000);
  };

  const stopRecording = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    try { recorderRef.current && recorderRef.current.state !== "inactive" && recorderRef.current.stop(); } catch (e) {}
  };

  const reRecord = () => {
    if (previewUrl) { try { URL.revokeObjectURL(previewUrl); } catch (e) {} }
    setPreviewUrl(null); blobRef.current = null; setElapsed(0); setStep("setup");
  };

  const save = async () => {
    const blob = blobRef.current;
    if (!blob || !blob.size) { toast.error("Nothing recorded yet."); return; }
    setStep("uploading"); setPct(0);
    try {
      const mime = blob.type || "video/webm";
      const ts = new Date().toLocaleString();
      const poster = await capturePoster(blob).catch(() => null);
      const { data } = await apiClient.post(`${base}/video-brief/init`, { filename: `Video brief - ${ts}`, mime });
      const uploadId = data.upload_id;
      for (let start = 0; start < blob.size; start += CHUNK) {
        const slice = blob.slice(start, Math.min(start + CHUNK, blob.size));
        await apiClient.post(`/video-brief/${uploadId}/chunk`, slice, { headers: { "Content-Type": "application/octet-stream" } });
        setPct(Math.min(99, Math.round(((start + CHUNK) / blob.size) * 100)));
      }
      await apiClient.post(`/video-brief/${uploadId}/complete`, { poster });
      setPct(100);
      toast.success("Video brief saved to Google Drive");
      cleanup();
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setStep("review");
    }
  };

  const close = () => { cleanup(); onClose(); };
  const isScreenCam = mode === "screen_camera";
  const showCamPicker = mode === "camera" || mode === "screen_camera";
  const showMicPicker = audioMix === "mic" || audioMix === "both";

  return (
    <Dialog open onOpenChange={close}>
      <DialogContent className="max-w-2xl bg-white" data-testid="video-brief-recorder">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Video className="h-5 w-5 text-[#5b2fd6]" /> Record a video brief</DialogTitle>
          <DialogDescription className="text-[#9FA1A4]">Record with your camera, share your screen, or both. It saves straight to this item's Google Drive folder.</DialogDescription>
        </DialogHeader>

        {!driveReady ? (
          <div className="rounded-xl border border-[#f0d9a8] bg-[#FFF8EC] px-4 py-5 text-sm text-[#7a5b1e]" data-testid="video-brief-drive-warning">
            <p className="font-medium mb-1">Connect Google Drive first</p>
            <p className="text-[13px]">Video briefs upload to the agency Google Drive. A super admin can connect it under <span className="font-medium">AI Agents → Integrations → Google Drive</span> and pick a Shared Drive. Once connected, this recorder will be ready.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* hidden compositing elements */}
            <video ref={screenVideoRef} className="hidden" muted playsInline />
            <video ref={camVideoRef} className="hidden" muted playsInline />
            <canvas ref={canvasRef} className="hidden" />

            {step === "review" ? (
              <video ref={reviewRef} src={previewUrl} controls className="w-full rounded-xl border border-[#EEEFEF] bg-black aspect-video" data-testid="video-brief-review" />
            ) : (
              <div className="relative">
                <video ref={liveRef} className="w-full rounded-xl border border-[#EEEFEF] bg-black aspect-video" muted playsInline autoPlay data-testid="video-brief-preview" />
                {step === "recording" && (
                  <div className="absolute top-3 left-3 flex items-center gap-1.5 rounded-full bg-black/70 px-2.5 py-1 text-white text-xs" data-testid="video-brief-timer">
                    <Circle className="h-2.5 w-2.5 fill-rose-500 text-rose-500 animate-pulse" /> {fmtTime(elapsed)} / {fmtTime(MAX_SECONDS)}
                  </div>
                )}
              </div>
            )}

            {error && <p className="text-xs text-rose-600" data-testid="video-brief-error">{error}</p>}

            {step === "setup" && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="text-[11px] text-[#9FA1A4] mb-1 block">Mode</label>
                  <Select value={mode} onValueChange={setMode}>
                    <SelectTrigger data-testid="video-brief-mode" className="h-9 bg-white border-[#EEEFEF] text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white border-[#EEEFEF]">{MODES.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-[11px] text-[#9FA1A4] mb-1 block">Audio</label>
                  <Select value={audioMix} onValueChange={setAudioMix}>
                    <SelectTrigger data-testid="video-brief-audio" className="h-9 bg-white border-[#EEEFEF] text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white border-[#EEEFEF]">{AUDIO_OPTS.map((a) => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-[11px] text-[#9FA1A4] mb-1 block">{showCamPicker ? "Camera" : "Mic"}</label>
                  {showCamPicker ? (
                    <Select value={camId} onValueChange={setCamId}>
                      <SelectTrigger data-testid="video-brief-camera" className="h-9 bg-white border-[#EEEFEF] text-sm"><SelectValue placeholder="Default camera" /></SelectTrigger>
                      <SelectContent className="bg-white border-[#EEEFEF]">{cams.map((c, i) => <SelectItem key={c.deviceId || i} value={c.deviceId || `cam-${i}`}>{c.label || `Camera ${i + 1}`}</SelectItem>)}</SelectContent>
                    </Select>
                  ) : (
                    <Select value={micId} onValueChange={setMicId}>
                      <SelectTrigger data-testid="video-brief-mic" className="h-9 bg-white border-[#EEEFEF] text-sm"><SelectValue placeholder="Default mic" /></SelectTrigger>
                      <SelectContent className="bg-white border-[#EEEFEF]">{mics.map((m, i) => <SelectItem key={m.deviceId || i} value={m.deviceId || `mic-${i}`}>{m.label || `Mic ${i + 1}`}</SelectItem>)}</SelectContent>
                    </Select>
                  )}
                </div>
                {showCamPicker && showMicPicker && (
                  <div className="sm:col-span-3">
                    <label className="text-[11px] text-[#9FA1A4] mb-1 block">Microphone</label>
                    <Select value={micId} onValueChange={setMicId}>
                      <SelectTrigger data-testid="video-brief-mic" className="h-9 bg-white border-[#EEEFEF] text-sm"><SelectValue placeholder="Default mic" /></SelectTrigger>
                      <SelectContent className="bg-white border-[#EEEFEF]">{mics.map((m, i) => <SelectItem key={m.deviceId || i} value={m.deviceId || `mic-${i}`}>{m.label || `Mic ${i + 1}`}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            )}

            {step === "uploading" && (
              <div className="rounded-xl bg-[#F6F6FD] border border-[#EEEFEF] px-4 py-3">
                <div className="flex items-center gap-2 text-sm text-[#5b2fd6]"><Loader2 className="h-4 w-4 animate-spin" /> Uploading to Google Drive… {pct}%</div>
                <div className="mt-2 h-1.5 w-full rounded-full bg-[#EEEFEF] overflow-hidden"><div className="h-full bg-[#5b2fd6] transition-all" style={{ width: `${pct}%` }} /></div>
              </div>
            )}

            <div className="flex items-center justify-end gap-2">
              {step === "setup" && <Button type="button" data-testid="video-brief-start" onClick={startRecording} className="bg-[#06060C] text-white hover:bg-[#1c1c24] rounded-full"><Circle className="h-4 w-4 mr-1.5 fill-rose-500 text-rose-500" /> {isScreenCam ? "Share screen & record" : "Start recording"}</Button>}
              {step === "recording" && <Button type="button" data-testid="video-brief-stop" onClick={stopRecording} className="bg-rose-600 text-white hover:bg-rose-700 rounded-full"><Square className="h-4 w-4 mr-1.5" /> Stop</Button>}
              {step === "review" && (
                <>
                  <Button type="button" variant="ghost" data-testid="video-brief-rerecord" onClick={reRecord} className="text-[#595D62] rounded-full"><RefreshCw className="h-4 w-4 mr-1.5" /> Re-record</Button>
                  <Button type="button" data-testid="video-brief-save" onClick={save} className="bg-[#06060C] text-white hover:bg-[#1c1c24] rounded-full">Save to Drive</Button>
                </>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
